import clsx from 'clsx';

import { Button } from '@commons/components/Button';
import Card from '@commons/components/Card';
import DrupalImage from '@commons/components/Image/Drupal';
import RichText from '@commons/components/RichText';
import Text, { type TextProps } from '@commons/components/Text';
import { Link } from '@commons/i18n/navigation';
import type {
  DrupalBlock,
  DrupalImage as DrupalImageType,
  DrupalLink,
} from '@commons/types/drupal';

import styles from './index.module.scss';

// Type definition for the CardBlock component properties.

type Props = DrupalBlock<{
  title: string;
  upperTitle: string;
  body: string;
  image: DrupalImageType;
  link?: DrupalLink;
  hasBorder?: boolean;
}>;

/**
 * Card block component.
 */

const CardBlock = ({ content }: Props) => {
  const { image, body, link, title, upperTitle, hasBorder } = content;

  const classes = clsx('w-100', 'h-100', 'has-nested-button');

  const card = (
    <Card hasBorder={hasBorder} disabled={!link} paddingBottom="xs">
      <Card.Content>
        {image && <DrupalImage {...image} />}
        {upperTitle && (
          <Text as="h6" level="label-md" transform="uppercase" marginBottom="none">
            {upperTitle}
          </Text>
        )}
        <CardTitle>{title}</CardTitle>
        {body && <RichText marginTop="xxs" value={body} />}
      </Card.Content>
      {link && <Button variant="primary">{link.text}</Button>}
    </Card>
  );

  if (link)
    return (
      <Link href={link.url} className={classes}>
        {card}
      </Link>
    );

  return card;
};

// Card title sub-component.

const CardTitle = ({
  as = 'h3',
  level = 'h4',
  transform,
  marginBottom = 'none',
  children,
  className,
  ...props
}: TextProps) => {
  const classes = clsx(styles.title, className);

  return (
    <Text
      as={as}
      level={level}
      transform={transform}
      marginBottom={marginBottom}
      className={classes}
      {...props}
    >
      {children}
    </Text>
  );
};

export default CardBlock;
